<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { useCourses } from '@/composables/queries/useCourses';
import {
  useListCoursesWithPrerequisitesQuery,
  useSetCoursePrerequisitesMutation,
  useRemoveCoursePrerequisiteMutation,
} from '@/types/vue-apollo-helpers';
import type { ListCoursesWithPrerequisitesQuery, ListCoursesWithPrerequisitesQueryVariables } from '@/types/operations';

type CourseWithPrereqs = ListCoursesWithPrerequisitesQuery['listSchoolCourses']['data'][number];

const toast = useToast();
const confirm = useConfirm();

const searchQuery = ref('');
const currentPage = ref(1);
const perPage = ref(20);
let searchDebounce: ReturnType<typeof setTimeout> | null = null;

function onSearch(val: string) {
  if (searchDebounce) clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    searchQuery.value = val;
    currentPage.value = 1;
  }, 400);
}

const queryVars = computed<ListCoursesWithPrerequisitesQueryVariables>(() => ({
  first: perPage.value,
  page: currentPage.value,
  filter: searchQuery.value ? { query: searchQuery.value } : undefined,
}));

const { result, loading, refetch } = useListCoursesWithPrerequisitesQuery(queryVars);

function onPageChange(e: { page?: number; rows?: number }) {
  if (e.rows && e.rows !== perPage.value) {
    perPage.value = e.rows;
    currentPage.value = 1;
  } else {
    currentPage.value = (e.page ?? 0) + 1;
  }
}

const coursesData = computed(() => result.value?.listSchoolCourses?.data ?? []);
const totalRecords = computed(() => result.value?.listSchoolCourses?.paginatorInfo?.total ?? 0);

const showSetDialog = ref(false);
const editingCourse = ref<{ id: string; course_code: string; course_title: string } | null>(null);
const selectedPrereqIds = ref<string[]>([]);

const { courses: allCourses, setFilter: setCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });
let courseDebounce: ReturnType<typeof setTimeout> | null = null;
function onCourseFilter(event: { originalEvent: Event; value: string }) {
  if (courseDebounce) clearTimeout(courseDebounce);
  courseDebounce = setTimeout(() => { setCourseFilter({ query: event.value }); }, 300);
}
const courseOptions = computed(() =>
  allCourses.value
    .filter((c) => c.id !== editingCourse.value?.id)
    .map((c) => ({
      label: `${c.course_code} — ${c.course_title} (${c.unit}u)`,
      value: c.id,
    })),
);

function openSetDialog(course: CourseWithPrereqs) {
  editingCourse.value = { id: course.id, course_code: course.course_code, course_title: course.course_title };
  selectedPrereqIds.value = course.prerequisites.map(p => p.prerequisite_course_id);
  showSetDialog.value = true;
}

function openAddDialog() {
  editingCourse.value = null;
  selectedPrereqIds.value = [];
  selectedCourseId.value = null;
  showSetDialog.value = true;
}

const selectedCourseId = ref<string | null>(null);
const targetCourseOptions = computed(() =>
  allCourses.value.map((c) => ({
    label: `${c.course_code} — ${c.course_title}`,
    value: c.id,
  })),
);

const { mutate: setPrereqs, loading: saving } = useSetCoursePrerequisitesMutation();
const { mutate: removePrereq } = useRemoveCoursePrerequisiteMutation();

async function handleSave() {
  const courseId = editingCourse.value?.id ?? selectedCourseId.value;
  if (!courseId || selectedPrereqIds.value.length === 0) return;
  try {
    await setPrereqs({
      course_id: courseId,
      prerequisite_course_ids: selectedPrereqIds.value,
    });
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Prerequisites updated', life: 3000 });
    showSetDialog.value = false;
    editingCourse.value = null;
    selectedCourseId.value = null;
    await refetch();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Failed to save prerequisites';
    toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 5000 });
  }
}

function confirmRemove(prereqId: string, courseCode: string, prereqCode: string) {
  confirm.require({
    message: `Remove ${prereqCode} as a prerequisite of ${courseCode}?`,
    header: 'Remove Prerequisite',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Remove',
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-danger',
    accept: async() => {
      try {
        await removePrereq({ id: prereqId });
        toast.add({ severity: 'success', summary: 'Removed', detail: 'Prerequisite removed', life: 3000 });
        await refetch();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to remove prerequisite';
        toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 5000 });
      }
    },
  });
}
</script>

<template>
  <div class="space-y-3">
    <div class="bg-primary-50 text-primary-700 p-3 rounded text-sm flex items-start gap-2">
      <i class="pi pi-info-circle mt-0.5"></i>
      <div>
        <p class="font-semibold">Course Prerequisites</p>
        <p>Define which courses must be passed before a student can register for another course. Prerequisites are enforced during registration when "Enforce Prerequisites" is enabled on the programme.</p>
      </div>
    </div>

    <div class="bg-surface-0 border border-surface-300 rounded-xl shadow-sm overflow-hidden">
      <div class="bg-surface-50 border-b border-surface-300 px-4 py-2.5 flex items-center justify-between">
        <span class="text-sm text-surface-600">
          <strong>{{ totalRecords }}</strong> course(s)
        </span>
        <div class="flex items-center gap-2">
          <InputText
            placeholder="Search courses..."
            class="text-sm w-64"
            @input="(e: Event) => onSearch((e.target as HTMLInputElement).value)"
          />
          <Button
            label="Add Prerequisite"
            icon="pi pi-plus"
            size="small"
            @click="openAddDialog"
          />
        </div>
      </div>

      <DataTable
        :value="coursesData"
        :loading="loading"
        size="small"
        stripedRows
        dataKey="id"
        :paginator="true"
        :rows="perPage"
        :totalRecords="totalRecords"
        :lazy="true"
        :rowsPerPageOptions="[20, 50, 100]"
        @page="onPageChange"
      >
        <template #empty>
          <div class="py-6 text-center text-surface-400 text-sm">
            {{ searchQuery ? 'No matching courses found.' : 'No courses found.' }}
          </div>
        </template>

        <Column field="course_code" header="Course Code" sortable style="width: 10rem">
          <template #body="{ data }">
            <span class="font-medium">{{ data.course_code }}</span>
          </template>
        </Column>

        <Column field="course_title" header="Course Title" sortable />

        <Column field="unit" header="Units" style="width: 5rem">
          <template #body="{ data }">
            <span class="bg-surface-100 text-surface-700 px-2 py-0.5 rounded text-xs font-bold">{{ data.unit }}</span>
          </template>
        </Column>

        <Column header="Prerequisites">
          <template #body="{ data }">
            <div v-if="data.prerequisites?.length" class="flex flex-wrap gap-1">
              <Tag
                v-for="p in data.prerequisites"
                :key="p.id"
                severity="info"
                v-tooltip.top="p.prerequisiteCourse?.course_title"
              >
                <span class="flex items-center gap-0.5">
                  {{ p.prerequisiteCourse?.course_code }}
                  <Button
                    icon="pi pi-times"
                    text
                    rounded
                    severity="danger"
                    class="!h-4 !w-4 !p-0 !text-[10px]"
                    v-tooltip.top="'Remove'"
                    @click.stop="confirmRemove(p.id, data.course_code, p.prerequisiteCourse?.course_code ?? '')"
                  />
                </span>
              </Tag>
            </div>
            <span v-else class="text-xs text-surface-400">None</span>
          </template>
        </Column>

        <Column header="" style="width: 4rem">
          <template #body="{ data }">
            <Button
              icon="pi pi-pencil"
              text
              rounded
              size="small"
              severity="secondary"
              v-tooltip.top="'Set prerequisites'"
              @click="openSetDialog(data)"
            />
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog v-model:visible="showSetDialog" modal header="Set Prerequisites" :style="{ width: '36rem' }">
      <div class="space-y-4">
        <div v-if="editingCourse" class="text-sm bg-surface-50 rounded-lg p-3">
          <span class="text-surface-500">Course:</span>
          <span class="font-semibold ml-1">{{ editingCourse.course_code }} — {{ editingCourse.course_title }}</span>
        </div>
        <div v-else class="flex flex-col gap-1">
          <label class="text-xs font-medium text-surface-700">Select Course</label>
          <Select
            v-model="selectedCourseId"
            :options="targetCourseOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Choose a course..."
            class="w-full"
            filter
            @filter="onCourseFilter"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-surface-700">Prerequisites (courses that must be passed first)</label>
          <MultiSelect
            v-model="selectedPrereqIds"
            :options="courseOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select prerequisite courses..."
            class="w-full"
            filter
            display="chip"
            @filter="onCourseFilter"
          />
        </div>
      </div>

      <template #footer>
        <Button label="Cancel" icon="pi pi-times" text size="small" @click="showSetDialog = false" />
        <Button
          label="Save"
          icon="pi pi-check"
          size="small"
          :loading="saving"
          :disabled="selectedPrereqIds.length === 0 || (!editingCourse && !selectedCourseId)"
          @click="handleSave"
        />
      </template>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>
